import * as lodash from 'lodash';

import {FileUtils} from '../Utils/FileUtils';
import {ArgValidator} from '../Utils/ArgValidator';
import {Utils} from '../Utils/Utils';
import {StringUtils} from '../Utils/StringUtils';
import {Logger} from '../Utils/Logger';
import {UriHelper} from '../Utils/UriHelper';
import {ICleanup} from '../Utils/CommonTypes';
import {HtmlUtils} from '../Utils/HtmlUtils';

import {PreloaderResourceItem} from './Types/PreloaderResourceItem';
import {PreloaderStatus} from './Types/PreloaderStatus';
import {IPreloader} from './Types/IPreloader';
import {PreloaderResourceLoader} from './Types/PreloaderResourceLoader';
import {PreloaderProviderBase} from './Types/PreloaderProviderBase';
import {PreloaderOptions} from './Types/PreloaderOptions';
import {PreloaderInfo} from './Types/PreloaderInfo';

const KEY_PRELOADER_QUALITY = 'PRELOADER_QUALITY';
const KEY_PRELOADER_PLATFORM = 'PRELOADER_PLATFORM';
const KEY_FORCE_FLAT_FILE_LIST = 'PRELOADER_FORCE_FLAT_FILE_LIST';
const KEY_GAME_SKIN = 'GAME_SKIN';

const DEFAULT_PRELOADER_QUALITY = 'FHD';
const DEFAULT_PRELOADER_PLATFORM = 'desktop';

const GROUP_TYPE_SHARED = 'shared';
const GROUP_TYPE_QUALITY = 'quality';
const GROUP_TYPE_PLATFORM = 'platform';

const RESOURCE_TYPE_RESOURCE = 'resource';
const RESOURCE_TYPE_XMLS = 'xmls';
const RESOURCE_TYPE_AUDIO = 'audio';

const BASE_GAME_GROUP = 'BASE';
const PRELOADER_GROUPS = ['SPLASH', 'BASE'];
const PRELOADER_GROUPS_ERROR = ['BASE']; //throw error for groups

type OrderedItem = {
    resItem: PreloaderResourceItem;
    order: number;
}

type ProgressInfo = {
    xmls_loaded?: number;
    normal_loaded?: number;
    audio_loaded?: number;
};

export class Preloader implements IPreloader, ICleanup {
    private static _versionId: string;
    private static _versionBuild: string;

    private _groups: Array<string>; //ordered list of groups to load (configurable)
    private _filesLoaded: number;
    private _filesToBaseLoad: number;
    private _allowUpdateProgress: boolean;

    private _skipAudioResources: boolean;
    private _fileName: string;
    private _versionFileName: string;
    private _fileXmlDoc: XMLDocument;
    private _orderedResources: Array<PreloaderResourceItem>;

    private _resourceLoader: PreloaderResourceLoader;
    private _audioResourceLoader: PreloaderResourceLoader;
    private _preloaderProvider: PreloaderProviderBase;

    private _progressTextElement: HTMLElement;
    private _progressBarContainerElement: HTMLElement;
    private _progressBarElement: HTMLElement;

    private _progressTotal: number;
    private _progressPercent: number;
    private _progressInfo: ProgressInfo = {
        xmls_loaded: null,
        normal_loaded: null,
        audio_loaded: null
    };

    private _loadingError: string;

    constructor(options: PreloaderOptions) {
        ArgValidator.ensureIsNotNullOrUndefined(options, 'options');
        ArgValidator.ensureStringIsNotNullOrEmpty(options.file, 'options.file');
        ArgValidator.ensureIsNotNullOrUndefined(options.preloaderProvider, 'options.preloaderProvider');
        ArgValidator.ensureIsNotNullOrUndefined(options.preloaderProvider.resourceLoader, 'options.resourceLoader');
        ArgValidator.ensureIsNotNullOrUndefined(options.preloaderProvider.audioResourceLoader, 'options.audioResourceLoader');
        ArgValidator.ensureStringIsNotNullOrEmpty(options.versionFile, 'options.versionFile');

        this._fileName = options.file;
        this._versionFileName = options.versionFile;
        this._preloaderProvider = options.preloaderProvider;
        this._resourceLoader = this._preloaderProvider.resourceLoader;
        this._audioResourceLoader = this._preloaderProvider.audioResourceLoader;
        this._groups = PRELOADER_GROUPS;

        if (this._resourceLoader.isAudioLoader) {
            throw new Error(`Parameter 'options.resourceLoader' points to audio loader, but must point to non-audio loader!`);
        }

        if (!this._audioResourceLoader.isAudioLoader) {
            throw new Error(`Parameter 'options.audioResourceLoader' points to non-audio loader, but must point to audio loader!`);
        }

        this._resourceLoader.init(this);
        this._audioResourceLoader.init(this);

        this._allowUpdateProgress = true;
        this._progressTotal = -1;
        this._loadingError = null;
        this._skipAudioResources = Utils.valueOrDefault(options.skipAudioResources, false);

        this._progressTextElement = StringUtils.isNullOrEmpty(options.progressTextElementId)
            ? null : document.getElementById(options.progressTextElementId);
        this._progressBarContainerElement = StringUtils.isNullOrEmpty(options.progressBarContainerElementId)
            ? null : document.getElementById(options.progressBarContainerElementId);
        this._progressBarElement = StringUtils.isNullOrEmpty(options.progressBarElementId)
            ? null : document.getElementById(options.progressBarElementId);

        // if container for progress bar is specified try to show it (in case it is hidden)
        if (this._progressBarContainerElement) {
            HtmlUtils.showElement(this._progressBarContainerElement);
        }

        this.loadVersionFile = this.loadVersionFile.bind(this);
        this.loadFileXml = this.loadFileXml.bind(this);
        this.parseFileList = this.parseFileList.bind(this);
        this.loadResources = this.loadResources.bind(this);
        this.loadNonAudioResources = this.loadNonAudioResources.bind(this);
        this.loadXmlsResources = this.loadXmlsResources.bind(this);
        this.loadAudioResources = this.loadAudioResources.bind(this);
        this.parseCurrentElements = this.parseCurrentElements.bind(this);
        this.loadAllGroupsLoaded = this.loadAllGroupsLoaded.bind(this);
        this.loadCompleted = this.loadCompleted.bind(this);
    }

    public get progressPercent(): number {
        return this._progressPercent;
    }

    /**
     * Gets version id
     * @returns {string} id of version.
     */
    public static getVersionId(): string {
        return Preloader._versionId;
    }

    /**
     * Gets version id
     * @returns {string} id of version.
     */
    public get versionId(): string {
        return Preloader._versionId;
    }

    /**
     * Gets version build info
     * @returns {string} version build info.
     */
    public static getVersionBuild(): string {
        return Preloader._versionBuild;
    }

    /**
     * Gets version build info
     * @returns {string} version build info.
     */
    public get versionBuild(): string {
        return Preloader._versionBuild;
    }

    /**
     * Gets flag whenever if loading of audio resources will be skipped or not.
     */
    public get skipAudioResources(): boolean {
        return this._skipAudioResources;
    }

    public set skipAudioResources(value: boolean) {
        this._skipAudioResources = value;
    }

    public loadVersion(): Promise<any> {
        if (!StringUtils.isNullOrEmpty(Preloader._versionId)) {
            return Promise.resolve(Preloader._versionId);
        }

        return this.loadVersionFile();
    }

    public load(loadingGroups: Array<string>): Promise<any> {
        this.reset();

        if (StringUtils.isNullOrEmpty(Preloader._versionId)) {
            throw new Error(`Version file needs to be loaded before loading other resources!`);
        }

        (PreloaderInfo as any).__updatePreloaderInfoAllowed = true;
        PreloaderInfo.instance._update({
            fileName: this._fileName,
            status: PreloaderStatus.InProgress,
            loadSucceed: false,
            resourceIdMappings: {}
        });

        this._groups = this.getLoadingGroups(loadingGroups);

        return this.loadFileXml()
            .then(this.parseFileList)
            .then(this.loadResources)
            .then(this.loadCompleted)
            .then(() => {
                (PreloaderInfo as any).__updatePreloaderInfoAllowed = true;
                PreloaderInfo.instance._update({
                    loadSucceed: true
                });
            })
            .catch(err => {
                this.handleLoadError(err);
            })
            .finally(() => {
                if (DEBUG) {
                    Logger.debugGroup('Preloader', 'All groups loaded');
                }

                (PreloaderInfo as any).__updatePreloaderInfoAllowed = true;
                PreloaderInfo.instance._update({
                    status: PreloaderStatus.Completed
                });
            });
    }

    private reset() {
        this._progressPercent = 0;
        this._fileXmlDoc = null;
        this._orderedResources = [];
        this._loadingError = null;
    }

    private getLoadingGroups(loadingGroups: Array<string>): Array<string> {
        let groups: Array<string> = [];

        if (loadingGroups != null && loadingGroups.length > 0) {
            for (let i = 0; i < PRELOADER_GROUPS.length; i++) {
                let currentGroup = PRELOADER_GROUPS[i];

                if (loadingGroups.indexOf(currentGroup) > -1 || currentGroup == BASE_GAME_GROUP) {
                    groups.push(currentGroup);
                }
            }
        }

        //BASE group is required
        if (groups.indexOf(BASE_GAME_GROUP) == -1) {
            groups.push(BASE_GAME_GROUP);
        }

        return groups;
    }

    private loadFileXml(): Promise<any> {
        var self = this;
        return FileUtils.readLocalXmlFile(UriHelper.setTimeStamp(this._fileName, self.versionId))
            .then(xmlDoc => {
                self._fileXmlDoc = xmlDoc;
            });
    }

    private loadVersionFile(): Promise<any> {
        return FileUtils.readLocalFile(UriHelper.setTimeStamp(this._versionFileName), null, false)
            .then(versionfileContent => {
                if (!StringUtils.isNullOrEmpty(versionfileContent)) {
                    var vals = versionfileContent.split('|');
                    if (vals && vals.length === 2) {
                        Preloader._versionBuild = vals[0];
                        Preloader._versionId = vals[1];
                    }
                }

                if (StringUtils.isNullOrEmpty(Preloader._versionId)) {
                    if (DEBUG) {
                        Logger.debugGroup('Preloader', `Could not found version from version.txt, using datetime now()!`);
                        Preloader._versionId = Date.now().toString();
                        Preloader._versionBuild = 'DEBUG-' + Date.now().toString();
                    } else {
                        throw new Error(`Cache file version content is empty!`);
                    }
                }

                return Preloader._versionId;
            });
    }

    private loadResources(): Promise<any> {
        //get all groups
        this._filesLoaded = 0;
        this._filesToBaseLoad = this.getFilesToBase();

        this._audioResourceLoader.beforeResourcesLoad();
        this._resourceLoader.beforeResourcesLoad();

        return new Promise<any>((resolve: any, reject: any) => {
            var _resolveFnc = resolve;
            var _rejectFnc = reject;

            return Promise.each(this._groups, group => {
                var fns = this._skipAudioResources
                    ? [this.loadNonAudioResources(group)]
                    : [this.loadNonAudioResources(group), this.loadAudioResources(group)];

                return Promise.all(fns)
                    .then(this.loadXmlsResources.bind(this, group))
                    .then(this.loadGroupCompleted.bind(this, group))
                    .then(this.parseCurrentElements)
                    .then(() => {
                        if (group == BASE_GAME_GROUP) {
                            if (_resolveFnc != null) {
                                _resolveFnc();
                            }
                        }
                    })
                    .catch(error => {
                        this.handleGroupLoadError(error, group);

                        if (PRELOADER_GROUPS_ERROR.indexOf(group) > -1) {
                            if (_resolveFnc != null) {
                                _rejectFnc(error);
                                throw error;
                            }
                        } else {
                            //else remove error for previous group if we dont want to handle it
                            this._loadingError = null;
                        }
                    });
            })
                .then(this.loadAllGroupsLoaded);
        });
    }

    private loadNonAudioResources(group: string): Promise<any> {
        var self = this;
        return new Promise<any>((resolve: Function, reject: Function) => {
            // self._resourceLoader.beforeResourcesLoad();

            // get resources to load (exlude audio files)
            var nonAudioResources = self.getNonAudioResources(group);
            if (nonAudioResources.length > 0) {
                var count = 0;
                self._resourceLoader.loadResources(nonAudioResources, function (error: any) {
                    count++;
                    self._updateTotalCounts(nonAudioResources[count]);
                }, function (error: any) {
                    if (error) {
                        reject(new Error(error));
                    }

                    resolve();
                });
            } else {
                resolve();
            }
        }).catch(error => {
            this.handleSingleLoadError(error);
        });
    }

    private loadAudioResources(group: string): Promise<any> {
        var self = this;
        return new Promise((resolve: Function, reject: Function) => {
            // self._audioResourceLoader.beforeResourcesLoad();

            // get audio resources to load
            var audioResources = self.getAudioResources(group);
            var count = 0;
            if (audioResources.length > 0) {
                self._audioResourceLoader.loadResources(audioResources, function (error: any) {
                    count++;
                    self._updateTotalCounts(audioResources[count]);
                }, function (error: any) {
                    if (error) {
                        reject(new Error(error));
                    }

                    resolve();
                });
            } else {
                resolve();
            }
        }).catch(error => {
            this.handleSingleLoadError(error);
        });
    }

    private loadXmlsResources(group: string): Promise<any> {
        var self = this;
        var xmlResources = this.getXmlsResources(group);

        return Promise.map(xmlResources, xmlRes => {
            return FileUtils.readLocalXmlFile(xmlRes.path, false)
                .then(xmlDoc => {
                    if (xmlDoc) {

                        if (DEBUG) {
                            Logger.debugGroup('Preloader', `Xmls file '${xmlRes.path}' was loaded`);
                        }

                        try {
                            self._resourceLoader.xmlsResourceLoaded(xmlDoc, xmlRes);
                        } catch (e) {
                            Logger.debugGroup('Preloader', `Error calling xmlFileLoaded for resource "${xmlRes.path}"!`);
                        }
                    } else {
                        if (DEBUG) {
                            Logger.errorGroup('Preloader', `Xmls file '${xmlRes.path}' loading error.`);
                        }

                        throw new Error(`Xmls file '${xmlRes.path}' loading error`);
                    }

                    self._updateTotalCounts(xmlRes);
                });
        }).catch(error => {
            this.handleSingleLoadError(error);
        });
    }

    private getFilesToBase(): number {
        let count = 0;

        for (let i = 0; i < this._groups.length; i++) {
            let group = this._groups[i];
            count += lodash.filter(this._orderedResources, resItem => resItem.group === group).length;

            if (group == BASE_GAME_GROUP) {
                break;
            }
        }

        return count;
    }

    private getXmlsResources(group: string): Array<PreloaderResourceItem> {
        return lodash.filter(this._orderedResources, resItem => resItem.type === RESOURCE_TYPE_XMLS && resItem.group === group);
    }

    private getNonAudioResources(group: string): Array<PreloaderResourceItem> {
        return lodash.filter(this._orderedResources, resItem => resItem.type === RESOURCE_TYPE_RESOURCE && resItem.group === group);
    }

    private getAudioResources(group: string): Array<PreloaderResourceItem> {
        return lodash.filter(this._orderedResources, resItem => resItem.type === RESOURCE_TYPE_AUDIO && resItem.group === group);
    }

    private _updateTotalCounts(file: any): void {
        if ( file !== undefined ) {
            this._filesLoaded++;
        }

        if (this._orderedResources != null) {
            const loaded = Utils.calcPercentage(this._filesLoaded, this._orderedResources.length, 0);
            this.updateProgress(loaded);
        }
    }

    public updateProgress(loaded: number): void {
        if (!this._allowUpdateProgress) {
            return;
        }

        if (loaded >= 100) {
            loaded = 100;
        }

        if (loaded > this._progressPercent) {
            this._progressPercent = loaded;
        }

        if (this._progressBarElement) {
            this._progressBarElement.style.width = this._progressPercent + '%';
        }

        if (this._progressTextElement) {
            this._progressTextElement.textContent = this._progressPercent + ' %';
        }

        if (this._preloaderProvider != null) {
            this._preloaderProvider.onUpdateProgress(this._progressPercent);
        }
    }

    private parseCurrentElements(): void {
        return this._preloaderProvider.parseCurrentElements();
    }

    private handleGroupLoadError(error: any, group: string): void {
        this._preloaderProvider.handleGroupError(error, group);
    }

    private handleLoadError(error: any): void {
        if (DEBUG) {
            Logger.errorGroup('Preloader', error);
        }

        //if group with required load failed, throw error and stop loading game
        this._loadingError = error;
        this.updateProgress(100); //fill loading bar before error
        this._allowUpdateProgress = false;
        throw new Error(this._loadingError);
    }

    private handleSingleLoadError(error: any): void {
        //if any resource failed to load, throw error (global loader will decide to stop loader)
        this._loadingError = error;
        throw new Error(this._loadingError);
    }

    private loadGroupCompleted(group: string): void {
        if (this._loadingError) {
            return;
        }

        this._preloaderProvider.loadGroupCompleted(group);
    }

    private loadCompleted(): Promise<any> {
        if (DEBUG) {
            Logger.debugGroup('Preloader', 'Main group loaded, loadCompleted');
        }
        this.updateProgress(99);
        return this._preloaderProvider.loadCompleted();
    }

    private loadAllGroupsLoaded(): void {
        this.updateProgress(100);

        this._resourceLoader.loadAllGroupsCompleted();
        this._audioResourceLoader.loadAllGroupsCompleted();
        this.cleanup();
    }

    private parseFileList() {
        ArgValidator.ensureIsNotNullOrUndefined(this._fileXmlDoc, 'fileXmlDoc');

        var loadGroupFileList = DEBUG;
        var foceFFL = window[KEY_FORCE_FLAT_FILE_LIST];
        if (foceFFL === true) {
            loadGroupFileList = false;
        }

        var gameSkin = window[KEY_GAME_SKIN];
        if (gameSkin == null) {
            gameSkin = 'default'
        }

        var _resourceIdMappings = {};

        if (loadGroupFileList) {
            var quality = window[KEY_PRELOADER_QUALITY];
            var platform = window[KEY_PRELOADER_PLATFORM];

            if (StringUtils.isNullOrEmpty(quality)) {
                Logger.errorGroup('Preloader', `missing value "${KEY_PRELOADER_QUALITY}" in window[], using default "${DEFAULT_PRELOADER_QUALITY}"`);
                quality = DEFAULT_PRELOADER_QUALITY;
            }
            if (StringUtils.isNullOrEmpty(platform)) {
                Logger.errorGroup('Preloader', `missing value "${KEY_PRELOADER_PLATFORM}" in window[], using default "${DEFAULT_PRELOADER_PLATFORM}"`);
                platform = DEFAULT_PRELOADER_PLATFORM;
            }

            Logger.debugGroup('Preloader', `[DEBUG] Start parsing group file list for quality "${quality}" and platform "${platform}"...`);

            var resources: Object = {
                [GROUP_TYPE_SHARED]: null,
                [GROUP_TYPE_PLATFORM]: {},
                [GROUP_TYPE_QUALITY]: {}
            };

            var elementFiles: Element = this._fileXmlDoc.firstChild as Element;
            var elementGroup: Element = elementFiles.firstElementChild;
            while (elementGroup != null) {

                if (elementGroup.hasAttribute('type')) {
                    var groupType = elementGroup.getAttribute('type');
                    var groupName = elementGroup.getAttribute('name');

                    if (Utils.valueIsIn(groupType, [GROUP_TYPE_SHARED, GROUP_TYPE_PLATFORM, GROUP_TYPE_QUALITY])) {
                        var isShared = groupType === GROUP_TYPE_SHARED;

                        if (!isShared && StringUtils.isNullOrEmpty(groupName)) {
                            throw new Error(`Group name is required for group type="${groupType}"!`);
                        }

                        var isValidPlatform = groupType === GROUP_TYPE_PLATFORM && groupName === platform;
                        var isValidQuality = groupType === GROUP_TYPE_QUALITY && groupName === quality;
                        var loadFiles = isShared || isValidPlatform || isValidQuality;

                        if (loadFiles) {
                            if (isShared && resources[GROUP_TYPE_SHARED] != null) {
                                throw new Error(`Detected multiple group of type="${GROUP_TYPE_SHARED}", only one group of such type is allowed!`);
                            }

                            var groupInfo = isShared ? GROUP_TYPE_SHARED : `${groupName} [${groupType}]`;
                            var elementFile: Element = this.getSkinElementsGroup(gameSkin, elementGroup);
                            while (elementFile != null) {
                                if (elementFile.hasAttribute('p')) {
                                    var p = this.applyTeplating(elementFile.getAttribute('p'), elementFile);
                                    p = UriHelper.setTimeStamp(p, this.versionId);
                                    var id = elementFile.hasAttribute('id') ? elementFile.getAttribute('id') : null;
                                    var pt = elementFile.hasAttribute('t') ? elementFile.getAttribute('t') : null;
                                    var group = elementFile.hasAttribute('g') ? elementFile.getAttribute('g') : 'BASE';

                                    if (StringUtils.isNullOrEmpty(pt)) {
                                        pt = RESOURCE_TYPE_RESOURCE;
                                    }

                                    var flags = this.getFlags(elementFile);

                                    if (!Utils.valueIsIn(pt, [RESOURCE_TYPE_RESOURCE, RESOURCE_TYPE_AUDIO, RESOURCE_TYPE_XMLS])) {
                                        throw new Error(`Unknown file type "${pt}" for file "${p}" !`);
                                    }

                                    var resItem: PreloaderResourceItem = {id: id, path: p, type: pt, flags: flags, group: group};
                                    if (isShared) {
                                        if (resources[GROUP_TYPE_SHARED] === null) {
                                            resources[GROUP_TYPE_SHARED] = {};
                                        }
                                    }

                                    if (resources[groupType].hasOwnProperty(resItem.path)) {
                                        throw new Error(`Resource "${resItem.path}" [${resItem.type}, id: '${resItem.id}'] already exist under "${groupType}" group!`);
                                    } else {
                                        resources[groupType][resItem.path] = resItem;
                                    }
                                } else {
                                    throw new Error(`Missing attribute "p" for element "f" wihtin group ${groupInfo}`);
                                }

                                elementFile = elementFile.nextElementSibling;
                            }
                        }
                    } else {
                        throw new Error(`Unknown group type "${groupType}"!`);
                    }
                } else {
                    throw new Error(`Missing group type!`);
                }

                elementGroup = elementGroup.nextElementSibling;
            }

            var resourceCountShared = lodash.size(resources[GROUP_TYPE_SHARED]);
            var resourceCountQuality = lodash.size(resources[GROUP_TYPE_QUALITY]);
            var resourceCountPlatform = lodash.size(resources[GROUP_TYPE_PLATFORM]);
            var allResourceCount = resourceCountShared + resourceCountQuality + resourceCountPlatform;

            Logger.debugGroup('Preloader', `Result group file list for quality "${quality}" and platform "${platform}": ` +
                `, Shared: ${resourceCountShared} resources` +
                `, Quality: ${resourceCountQuality} resources` +
                `, Platform: ${resourceCountPlatform} resources (total ${allResourceCount} resources)`);

            if (resourceCountQuality === 0) {
                Logger.errorGroup('Preloader', `There arent any "${GROUP_TYPE_QUALITY}" resources found!`);
            }

            if (resourceCountPlatform === 0) {
                Logger.errorGroup('Preloader', `There arent any "${GROUP_TYPE_PLATFORM}" resources found!`);
            }

            // start from shared resources and rewrite when found in other resources group
            var tempResources: Object = {};
            for (var sharedResPath in resources[GROUP_TYPE_SHARED]) {
                if (resources[GROUP_TYPE_SHARED].hasOwnProperty(sharedResPath)) {
                    tempResources[sharedResPath] = {
                        resItem: resources[GROUP_TYPE_SHARED][sharedResPath],
                        order: -1
                    };
                }
            }

            var resourceIndex = 0;
            for (var groupType in resources) {
                if (resources.hasOwnProperty(groupType) && (groupType !== GROUP_TYPE_SHARED)) {
                    var groupResources = resources[groupType];

                    for (var resPath in groupResources) {
                        if (groupResources.hasOwnProperty(resPath)) {
                            var resItem: PreloaderResourceItem = groupResources[resPath];

                            if (tempResources.hasOwnProperty(resItem.path)) {
                                Logger.debugGroup('Preloader', `Resource "${resItem.path}" [${resItem.type}, id: '${resItem.id}'] replacing shared resource.`);
                            }

                            tempResources[resItem.path] = {
                                resItem: resItem,
                                order: this.getOrderIndex(resItem.group, resItem.type)
                            };

                            resourceIndex++;
                        }
                    }
                }
            }

            this._orderedResources = lodash.map(lodash.orderBy(lodash.values<OrderedItem>(tempResources), [x => x.order, x => x.resItem.path], ['desc', 'desc']),
                x => {
                    if (x.resItem.id) {
                        _resourceIdMappings[x.resItem.id] = x.resItem.path;
                    }
                    return x.resItem;
                });

        } else {
            var elementFiles: Element = this._fileXmlDoc.firstChild as Element;
            var elementFile: Element = this.getSkinElementsGroup(gameSkin, elementFiles);

            var tempResources: Object = {};
            var resourceIndex = 0;
            while (elementFile != null) {
                if (elementFile.hasAttribute('p')) {
                    var p = this.applyTeplating(elementFile.getAttribute('p'), elementFile);
                    p = UriHelper.setTimeStamp(p, this.versionId);
                    var id = elementFile.hasAttribute('id') ? elementFile.getAttribute('id') : null;
                    var pt = elementFile.hasAttribute('t') ? elementFile.getAttribute('t') : null;
                    var group = elementFile.hasAttribute('g') ? elementFile.getAttribute('g') : 'BASE';

                    if (StringUtils.isNullOrEmpty(pt)) {
                        pt = RESOURCE_TYPE_RESOURCE;
                    }
                    var flags = this.getFlags(elementFile);

                    if (!Utils.valueIsIn(pt, [RESOURCE_TYPE_RESOURCE, RESOURCE_TYPE_AUDIO, RESOURCE_TYPE_XMLS])) {
                        throw new Error(`Unknown file type "${pt}" for file "${p}" !`);
                    }

                    var resItem: PreloaderResourceItem = {id: id, path: p, type: pt, flags: flags, group: group};

                    if (tempResources.hasOwnProperty(resItem.path)) {
                        throw new Error(`Resource "${resItem.path}" [${resItem.type}, id: '${resItem.id}'] already exist under "${groupType}" group!`);
                    } else {
                        tempResources[resItem.path] = {
                            resItem: resItem,
                            order: this.getOrderIndex(resItem.group, resItem.type)
                        };
                    }

                    resourceIndex++;
                } else {
                    throw new Error(`Missing attribute "p" for element "f"`);
                }

                elementFile = elementFile.nextElementSibling;
            }

            this._orderedResources = lodash.map(lodash.orderBy(lodash.values<OrderedItem>(tempResources), [x => x.order, x => x.resItem.path], ['desc', 'desc']),
                x => {
                    if (x.resItem.id) {
                        _resourceIdMappings[x.resItem.id] = x.resItem.path;
                    }
                    return x.resItem;
                });
        }

        (PreloaderInfo as any).__updatePreloaderInfoAllowed = true;
        PreloaderInfo.instance._update({
            resourceIdMappings: _resourceIdMappings
        });

        if (this._orderedResources.length === 0) {
            throw new Error('Files list is empty!');
        }

        this._preloaderProvider.prepareFileList(this._orderedResources);

        // prepare progress data
        this._progressPercent = 0;
    }

    private getSkinElementsGroup(skin: string, elementsGroup: Element): Element {
        let result = document.createElement('group');
        let files = elementsGroup.querySelectorAll('f');
        let skinFiles = elementsGroup.querySelectorAll(`f[gameSkin="${skin}"]`);

        if (skinFiles.length == 0) {
            skin = 'default';
        }

        for (let i = 0; i < files.length; i++) {
            if (files[i].hasAttribute('gameSkin')) {
                const fileSkin = files[i].getAttribute('gameSkin');
                if (fileSkin != skin) {
                    continue;
                }
            }

            result.appendChild(files[i]);
        }

        return result.firstElementChild;
    }

    private getOrderIndex(group: string, type: string): number {
        let index = 1;

        if (this._groups != null && this._groups.length > 0) {
            if (this._groups.indexOf(group) > -1) {
                index = this._groups.length - this._groups.indexOf(group);
            }
        }

        return index;
    }

    private applyTeplating(content: string, elementFile: Element): string {
        if (!StringUtils.isNullOrEmpty(content)) {
            var regexPatter = /\{([^}]+)\}/gmi;
            content = content.replace(regexPatter, (fullMatch: string, match: string) => {
                var attrKey = 'd-' + match;
                if (elementFile.hasAttribute(attrKey)) {
                    var value = elementFile.getAttribute(attrKey);

                    if (value == 'QUALITY' && !StringUtils.isNullOrEmpty(window[KEY_PRELOADER_QUALITY])) {
                        value = window[KEY_PRELOADER_QUALITY];
                    } else if (value == 'PLATFORM' && !StringUtils.isNullOrEmpty(window[KEY_PRELOADER_PLATFORM])) {
                        value = window[KEY_PRELOADER_PLATFORM];
                    }

                    fullMatch = Utils.valueOrDefault(value, '');
                }

                return fullMatch;
            });
        }

        return content;
    }

    private getFlags(elementFile: Element): Array<string> {
        var result: Array<string> = [];

        if (elementFile.hasAttribute('x')) {
            var flags = elementFile.getAttribute('x');
            if (!StringUtils.isNullOrEmpty(flags)) {
                var items = flags.split(';');
                lodash.each(items, item => {
                    item = lodash.trim(item);
                    if (!StringUtils.isNullOrEmpty(item)) {
                        result.push(item);
                    }
                });
            }
        }

        return result;
    }

    public cleanup(): void {
        this._fileXmlDoc = null;
        if (this._orderedResources) {
            this._orderedResources.length = 0;
        }
        this._orderedResources = null;

        if (PreloaderInfo.instance.resourcesLoaded && this._resourceLoader) {
            this._resourceLoader.cleanup();
            this._resourceLoader = null;
        }

        if (PreloaderInfo.instance.audioLoaded && this._audioResourceLoader) {
            this._audioResourceLoader.cleanup();
            this._audioResourceLoader = null;
        }

        if (this._preloaderProvider) {
            this._preloaderProvider.cleanup();
        }
        this._preloaderProvider = null;

        this._progressTextElement = null;
        this._progressBarContainerElement = null;
        this._progressBarElement = null;
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Preloader/Preloader.ts
 **/